"use client";

import { dec } from "@/lib/num";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Boxes, CheckCircle2, ChevronsLeft, ChevronsRight, Database, FileText, Gauge, GitBranch, Inbox, LayoutGrid, Network, Repeat, TrendingUp, UserMinus } from "lucide-react";

import { useAuth } from "@/components/auth-provider";
import { hasPermission, deptUsesTabPerms, canViewTab } from "@/lib/access";
import { api } from "@/lib/api";
import { consumeOpenDeptDashboard, activeModuleStorageKey, readActiveSub, writeActiveSub, migrateLegacyActiveSub, OPEN_MODULE_EVENT, publishActiveSection } from "@/lib/dept-nav";
// Сам бар разделов убран (структура PLUX — навигация слева), но типы вкладок
// остаются общими с ModuleTabs: их же формат публикуется в сайдбар.
import { type ModuleTabItem, type ModuleTabSubItem } from "@/components/ui/module-tabs";
import { setActiveView } from "@/lib/active-view";

// Лейбл под-вкладки по ключу (рекурсивно, т.к. под-вкладки могут иметь свои под-вкладки).
function findSubLabel(subs: ModuleTabSubItem[] | undefined, key: string): string {
  for (const s of subs ?? []) {
    if (s.key === key) return s.label;
    const deep = findSubLabel(s.subItems, key);
    if (deep) return deep;
  }
  return "";
}
import {
  MODULE_SUBTABS as LIB_SUBTABS, SALES_SYNTH, sortSalesTabs, isHiddenSalesSub,
  DASHBOARD_KEY, REPORT_KEY, FINANCE_KEY, PRODUCTS_KEY, WAREHOUSE_KEY,
  X10_KEY, MPPULSE_KEY, CREATORS_KEY, COMMERCE_KEY, BARTERS_KEY, BLOGGERS_KEY, BARTERERS_KEY, INTEGRATIONS_KEY,
  TABBED_DEPARTMENTS, hasDeptDashboard,
  type TabNode,
} from "@/lib/dept-tabs";
import { visibleDeptTabs, resolveSubKey } from "@/lib/dept-sections";
import { Segmented } from "@/components/ui/segmented";
import type { Department, PortalModule, WbReviewSummary } from "@/lib/types";
import { MODULE_ICON, MODULE_COLOR, MODULE_FALLBACK_ICON, MODULE_FALLBACK_COLOR, type IconType } from "@/lib/module-icons";
import { AppEmbedsPanel, AppEmbedViewer } from "./app-embeds-panel";
import { appTabKey, parseAppTabKey, type AppEmbed } from "@/lib/app-embeds";
import { IconChevronLeft, IconChevronRight, IconCoins, IconDashboard, IconPackage, IconPlane, IconSearch, IconSpinner, IconStar, IconTasks, IconTruck, IconUsers, IconWarehouse, IconWorkflow } from "@/components/ui/icons";
import { Z } from "@/lib/z";
import { TabPanel } from "@/components/ui/tab-panel";
import { runTabTransition, tabDirection, type TabDirection } from "@/lib/view-transition";
import { TabHeader, ScreenTitleProvider } from "@/components/ui/tab-header";

// ── Lazy-loaded module workspaces ─────────────────────────────────────────────

const TelegramWorkspace = dynamic(
  () => import("@/components/telegram/telegram-workspace").then((m) => ({ default: m.TelegramWorkspace })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const WbReviewsWorkspace = dynamic(
  () => import("@/components/wb-reviews/wb-reviews-workspace").then((m) => ({ default: m.WbReviewsWorkspace })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const SalesWorkspace = dynamic(
  () => import("@/components/sales/sales-workspace").then((m) => ({ default: m.SalesWorkspace })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const LabelMakerWorkspace = dynamic(
  () => import("@/components/label-maker/label-maker-workspace").then((m) => ({ default: m.LabelMakerWorkspace })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const RoadmapBoard = dynamic(
  () => import("@/components/roadmap/roadmap-board").then((m) => ({ default: m.RoadmapBoard })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const FlowBuilderPage = dynamic(
  () => import("@/components/telegram/flow-builder").then((m) => ({ default: m.FlowBuilderPage })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const AssortmentPage = dynamic(
  () => import("@/components/assortment/assortment-page").then((m) => ({ default: m.AssortmentPage })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const PlanYucPage = dynamic(
  () => import("@/components/plan-yuc/plan-yuc-page").then((m) => ({ default: m.PlanYucPage })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const RdCollectionPage = dynamic(
  () => import("@/components/rd-collection/rd-collection-page").then((m) => ({ default: m.RdCollectionPage })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const ProductLaunchPage = dynamic(
  () => import("@/components/product-launch/product-launch-page").then((m) => ({ default: m.ProductLaunchPage })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const DevTrackerPage = dynamic(
  () => import("@/components/dev-tracker/dev-tracker-page").then((m) => ({ default: m.DevTrackerPage })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const HrPage = dynamic(
  () => import("@/components/hr/hr-page").then((m) => ({ default: m.HrPage })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
/** В отделе IT доски личных задач больше нет — она в «Кабинете» (01.09.2026).
 *  Здесь остаются виджеты отдела: заявки на доработку, переезд отделов,
 *  календарь и база знаний. */
const ItTasksOnlyWidgets = dynamic(
  () => import("@/components/it-tasks/it-tasks-page").then((m) => {
    const C = m.ItTasksPage;
    const W = (props: Record<string, unknown>) => <C {...props} only="widgets" />;
    W.displayName = "ItTasksOnlyWidgets";
    return { default: W };
  }),
  { ssr: false },
);
const ItTasksPage = dynamic(
  () => import("@/components/it-tasks/it-tasks-page").then((m) => ({ default: m.ItTasksPage })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const ContentFactoryPage = dynamic(
  () => import("@/components/content-factory/content-factory-page").then((m) => ({ default: m.ContentFactoryPage })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const WorkflowsPage = dynamic(
  () => import("@/components/workflows/workflows-page").then((m) => ({ default: m.WorkflowsPage })),
  { ssr: false, loading: () => <ModuleLoader /> },
);

const CreatorReportsWorkspace = dynamic(
  () => import("@/components/creator-reports/creator-reports-workspace").then((m) => ({ default: m.CreatorReportsWorkspace })),
  { ssr: false, loading: () => <ModuleLoader /> },
);

// Вынесенные верхнеуровневые вкладки маркетинга «Коммерция»/«Бартеры» = MarketingTab
// с фиксированным каналом (без дропдаупа выбора канала).
const MarketingTab = dynamic(
  () => import("@/components/creator-reports/marketing-tab").then((m) => ({ default: m.MarketingTab })),
  { ssr: false, loading: () => <ModuleLoader /> },
);

// Вкладки Блогеры/Бартеры: «База» (справочник) + «Закупки» (интеграции своего типа).
const PartnerTab = dynamic(
  () => import("@/components/creator-reports/partner-tab").then((m) => ({ default: m.PartnerTab })),
  { ssr: false, loading: () => <ModuleLoader /> },
);

// Воронка интеграций (Маркетинг → Интеграции).
const IntegrationsTab = dynamic(
  () => import("@/components/creator-reports/integrations-tab").then((m) => ({ default: m.IntegrationsTab })),
  { ssr: false, loading: () => <ModuleLoader /> },
);

const SalesDashboard = dynamic(
  () => import("@/components/dashboard/sales-dashboard").then((m) => ({ default: m.SalesDashboard })),
  { ssr: false, loading: () => <ModuleLoader /> },
);

const SalesReportTab = dynamic(
  () => import("@/components/sales/sales-report-tab").then((m) => ({ default: m.SalesReportTab })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const IncomingGoodsTab = dynamic(
  () => import("@/components/sales/incoming-goods-tab").then((m) => ({ default: m.IncomingGoodsTab })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const SeasonAssortmentTab = dynamic(
  () => import("@/components/sales/season-assortment-tab").then((m) => ({ default: m.SeasonAssortmentTab })),
  { ssr: false, loading: () => <ModuleLoader /> },
);
const WarehouseStockTab = dynamic(
  () => import("@/components/sales/warehouse-stock-tab").then((m) => ({ default: m.WarehouseStockTab })),
  { ssr: false, loading: () => <ModuleLoader /> },
);

const RdDashboard = dynamic(
  () => import("@/components/dashboard/rd-dashboard").then((m) => ({ default: m.RdDashboard })),
  { ssr: false, loading: () => <ModuleLoader /> },
);

const MarketingDashboard = dynamic(
  () => import("@/components/dashboard/marketing-dashboard").then((m) => ({ default: m.MarketingDashboard })),
  { ssr: false, loading: () => <ModuleLoader /> },
);

// ── Единый реестр модулей ─────────────────────────────────────────────────────
// ОДНА запись на модуль: компонент (lazy), иконка, цвет, размещение.
// MODULE_COMPONENTS / TABBED_MODULE_KEYS / STANDALONE_MODULES — производные.
// Новый модуль = запись здесь + подвкладки в lib/dept-tabs MODULE_SUBTABS (если
// есть) + строка модуля в бэкенде (+ тонкий роут app/modules/<key>/page.tsx).

type ModuleComponentType = React.ComponentType;

type ModuleDef = {
  /** Лениво загружаемый компонент. Без него модуль виден только карточкой (users). */
  component?: ModuleComponentType;
  icon: IconType;
  color: string;
  /** Рендерится верхней ВКЛАДКОЙ отдела (иначе — карточка в правой панели). */
  tabbed?: boolean;
  /** Доступен только по своему роуту, из вкладок/панели отдела исключён. */
  standalone?: boolean;
};

// Иконка и цвет каждого модуля берутся из ЕДИНОГО реестра lib/module-icons
// (тот же источник читают /modules, главная и навигация) — здесь добавляем
// только component/tabbed/standalone.
const modDef = (key: string, extra: Omit<ModuleDef, "icon" | "color">): ModuleDef => ({
  icon: MODULE_ICON[key],
  color: MODULE_COLOR[key],
  ...extra,
});

const MODULE_REGISTRY: Record<string, ModuleDef> = {
  bots: modDef("bots", { component: TelegramWorkspace as ModuleComponentType }),
  reviews: modDef("reviews", { component: WbReviewsWorkspace as ModuleComponentType }),
  sales: modDef("sales", { component: SalesWorkspace as ModuleComponentType, tabbed: true }),
  labels: modDef("labels", { component: LabelMakerWorkspace as ModuleComponentType, tabbed: true }),
  roadmap: modDef("roadmap", { component: RoadmapBoard as ModuleComponentType, standalone: true }),
  users: modDef("users", {}),
  creators: modDef("creators", { component: CreatorReportsWorkspace as ModuleComponentType, tabbed: true }),
  assortment: modDef("assortment", { component: AssortmentPage as ModuleComponentType, tabbed: true }),
  planning: modDef("planning", { component: PlanYucPage as ModuleComponentType, tabbed: true }),
  purchases: modDef("purchases", { component: RdCollectionPage as ModuleComponentType, tabbed: true }),
  product_launch: modDef("product_launch", { component: ProductLaunchPage as ModuleComponentType, tabbed: true }),
  // Модули «IT-проекты» (it_projects) и «Дашборд IT» (it_dashboard) удалены из портала
  // по просьбе Олега (2026-07-28): роуты, страницы, права и пункты навигации убраны.
  // У отдела IT (ai) остаётся ОДНА вкладка — «Задачи», самостоятельная и без ссылок на убранное.
  // ⚠️ «ЗАДАЧИ» УБРАНЫ ИЗ ОТДЕЛА IT (Максим, 04.09.2026: «вот это нам больше не
  // нужно, ведь мы всё перенесли в кабинет»). Доска задач, перевод отделов,
  // заявки на доработку и материалы теперь живут в кабинете и работают там
  // самостоятельно — открыть заявку и завести ссылку можно на месте.
  // standalone: страница /modules/tasks ЖИВА и открывается по прямой ссылке —
  // если чего-то не хватило, вход остался. Право `tab:it:tasks` тоже осталось:
  // им гейтятся блоки в кабинете, снимать его нельзя.
  tasks: modDef("tasks", { component: ItTasksOnlyWidgets as ModuleComponentType, tabbed: true, standalone: true }),
  dev_tracker: modDef("dev_tracker", { component: DevTrackerPage as ModuleComponentType, tabbed: true }),
  images: modDef("images", { component: ContentFactoryPage as ModuleComponentType }),
  workflows: modDef("workflows", { component: WorkflowsPage as ModuleComponentType }),
  // HR — не-tabbed отдел: модуль сам рисует 6-табовый интерфейс (как wb_reviews).
  hr: modDef("hr", { component: HrPage as ModuleComponentType }),
};

// Производные представления реестра — руками НЕ редактировать, править MODULE_REGISTRY.
export const MODULE_COMPONENTS: Record<string, ModuleComponentType> = Object.fromEntries(
  Object.entries(MODULE_REGISTRY).flatMap(([k, d]) => (d.component ? [[k, d.component] as const] : [])),
);
const TABBED_MODULE_KEYS = new Set(Object.keys(MODULE_REGISTRY).filter((k) => MODULE_REGISTRY[k].tabbed));
// Модули, исключённые из вкладок/панели отдела (доступны по своему роуту).
/** Модули, которые видит ОТДЕЛ. Один отбор на страницу отдела и на сайдбар:
 *  списки обязаны совпадать, иначе в меню будут пункты, которых нет на странице. */
export function pickDeptModules<T extends { key: string; departmentKeys: string[] }>(
  modules: T[], deptKey: string, user: Parameters<typeof hasPermission>[0],
): T[] {
  return modules.filter(
    (m) =>
      !STANDALONE_MODULES.has(m.key) &&
      m.departmentKeys.includes(deptKey) &&
      // В tab-режиме отдела гейтинг переносится на уровень вкладок (Фаза 2),
      // поэтому модуль здесь не отсекаем по module-view; иначе — прежнее правило.
      (deptUsesTabPerms(user, deptKey) || hasPermission(user, m.key, "view")) &&
      m.key in MODULE_COMPONENTS,
  );
}

export const STANDALONE_MODULES = new Set(Object.keys(MODULE_REGISTRY).filter((k) => MODULE_REGISTRY[k].standalone));

// Дерево суб-табов — ЕДИНЫЙ ИСТОЧНИК в lib/dept-tabs (его же читают права/enforcement).
// Здесь только адаптируем shape (children → subItems) и навешиваем иконки подвкладок.
const SUBTAB_ICONS: Record<string, IconType> = {
  orders: IconPackage, pivot: GitBranch, logistics: IconTruck, msklad: Inbox, costprice: IconCoins, refs: Database,
  digitization: Gauge, opiu: FileText,
};
const toSubItems = (nodes?: TabNode[]): ModuleTabSubItem[] | undefined =>
  nodes?.map((n) => ({ key: n.key, label: n.label, icon: SUBTAB_ICONS[n.key], subItems: toSubItems(n.children) }));
const MODULE_SUBTABS: Record<string, ModuleTabSubItem[]> = Object.fromEntries(
  Object.entries(LIB_SUBTABS).map(([k, v]) => [k, toSubItems(v) ?? []]),
);

// Псевдо-ключи вкладок и наборы tabbed-отделов импортируются из lib/dept-tabs
// (единый источник с матрицей прав) — локальных копий здесь больше нет.

// Псевдо-ключи, для которых нужно резолвить какой именно отдел/модуль рендерить.
const PSEUDO_KEYS = new Set<string>([
  DASHBOARD_KEY,
  REPORT_KEY,
  CREATORS_KEY,
  COMMERCE_KEY,
  BARTERS_KEY,
  BLOGGERS_KEY,
  BARTERERS_KEY,
  INTEGRATIONS_KEY,
  MPPULSE_KEY,
  X10_KEY,
  FINANCE_KEY,
  PRODUCTS_KEY,
  WAREHOUSE_KEY,
]);

// Декор (иконка/цвет) синтет-вкладок Продаж. Структуру/подвкладки/условие берём из lib SALES_SYNTH.
const SYNTH_DECOR: Record<string, { icon: IconType; color: string }> = {
  [REPORT_KEY]: { icon: FileText, color: "#7C3AED" },
  [MPPULSE_KEY]: { icon: Gauge, color: "#22C55E" },
  [X10_KEY]: { icon: TrendingUp, color: "#10B981" },
  [FINANCE_KEY]: { icon: IconCoins, color: "#F59E0B" },
  [PRODUCTS_KEY]: { icon: Boxes, color: "#A855F7" },
  [WAREHOUSE_KEY]: { icon: IconWarehouse, color: "#14B8A6" },
};

// Декор синтет-вкладок отдела HR (7 разделов модуля `hr`). Ключи — из MODULE_SUBTABS.hr.
// Иконки — из пака (lucide только там, где в паке аналога нет: увольнение).
const HR_DECOR: Record<string, { icon: IconType; color: string }> = {
  dashboard: { icon: IconDashboard, color: "var(--primary)" },
  org: { icon: IconWorkflow, color: "#8B5CF6" },
  hiring: { icon: IconSearch, color: "#F43F5E" },
  onboarding: { icon: IconTasks, color: "#10B981" },
  offboarding: { icon: UserMinus, color: "#F59E0B" },
  staff: { icon: IconUsers, color: "#0EA5E9" },
  vacations: { icon: IconPlane, color: "#06B6D4" },
};

const STATUS_LABELS: Record<string, string> = {
  active: "Активен",
  in_progress: "В разработке",
  planned: "Планируется",
};
const STATUS_COLORS: Record<string, string> = {
  active: "bg-[color-mix(in_srgb,var(--success)_15%,transparent)] text-[var(--success)] dark:text-[var(--success)]",
  in_progress: "bg-[color-mix(in_srgb,var(--warning)_15%,transparent)] text-[var(--warning)] dark:text-[var(--warning)]",
  planned: "bg-[var(--secondary)] text-[var(--textMuted)]",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function ModuleLoader() {
  return (
    <div className="flex h-64 items-center justify-center text-[var(--textMuted)]">
      <IconSpinner size={22} className="animate-spin" />
    </div>
  );
}

// ── Module card ───────────────────────────────────────────────────────────────

function ModuleCard({
  module,
  active,
  onClick,
  reviewsSummary,
}: {
  module: PortalModule;
  active: boolean;
  onClick: () => void;
  reviewsSummary?: WbReviewSummary | null;
}) {
  const meta = MODULE_REGISTRY[module.key] ?? { icon: MODULE_FALLBACK_ICON, color: MODULE_FALLBACK_COLOR };
  const Icon = meta.icon;
  const showStats = module.key === "reviews" && reviewsSummary;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-[var(--radius-lg)] border p-3.5 transition-all ${active ? "seg seg-on" : "seg"}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[var(--radius-md)]"
          style={{ backgroundColor: meta.color + "22", color: meta.color }}
        >
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-[12px] font-semibold leading-tight ${active ? "text-[var(--primary)]" : "text-[var(--text)]"}`}>
            {module.title}
          </div>
          {!showStats && (
            <>
              <div className="mt-1 text-[11px] text-[var(--textMuted)] line-clamp-2 leading-relaxed">
                {module.description}
              </div>
              <div className={`mt-2 inline-flex items-center rounded-[var(--radius-md)] px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[module.status] ?? STATUS_COLORS.planned}`}>
                {STATUS_LABELS[module.status] ?? module.status}
              </div>
            </>
          )}
        </div>
      </div>
      {showStats && (
        <div className="mt-3 grid grid-cols-4 gap-1">
          {([
            { icon: FileText, value: reviewsSummary.totalReviews, title: "Всего отзывов", color: "#6366F1" },
            { icon: AlertCircle, value: reviewsSummary.pendingReviews, title: "Ждут публикации", color: "#EF4444" },
            { icon: CheckCircle2, value: reviewsSummary.publishedReviews, title: "Опубликовано", color: "#10B981" },
            { icon: IconStar, value: dec(reviewsSummary.averageRating, 2), title: "Средний рейтинг", color: "#F59E0B" },
          ] as const).map(s => (
            <div key={s.title} data-tooltip={s.title} className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--secondary)] px-1 py-1 text-center">
              <div className="flex justify-center mb-0.5"><s.icon size={10} color={s.color} /></div>
              <div className="text-[11px] font-semibold leading-tight">{s.value}</div>
            </div>
          ))}
        </div>
      )}
    </button>
  );
}

// ── Department overview (shown when no module is active) ──────────────────────

function DepartmentOverview({
  dept,
  moduleCount,
}: {
  dept: Department;
  moduleCount: number;
}) {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[320px] text-center px-8">
      <IconDashboard size={44} className="mb-4 text-[var(--textMuted)]" />
      <h2 className="text-[20px] font-semibold text-[var(--text)]">{dept.title}</h2>
      <p className="mt-2 text-[12px] text-[var(--textMuted)]">Руководитель: {dept.head}</p>
      <p className="mt-4 text-[12px] text-[var(--textMuted)]">
        {moduleCount > 0
          ? `Выберите модуль из панели справа (${moduleCount} доступно)`
          : "Нет доступных модулей для этого отдела"}
      </p>
    </div>
  );
}

// ── Department dashboard (вкладка «Дашборд» в tabbed-отделах) ─────────────────
// Пока заглушка. В будущем — KPI отдела, сводка по модулям, активность.
function DepartmentDashboard({
  dept,
  modules,
}: {
  dept: Department | null;
  modules: PortalModule[];
}) {
  return (
    <div className="space-y-4">
      <div>
        <div className="field-label mb-1">Отдел</div>
        <h1 className="text-[20px] font-bold text-[var(--text)]">{dept?.title ?? "—"}</h1>
        {dept?.head && (
          <p className="mt-1 text-[12px] text-[var(--textMuted)]">Руководитель: {dept.head}</p>
        )}
      </div>

      <div>
        <div className="field-label mb-2">
          Модули отдела ({modules.length})
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {modules.map((m) => {
            const meta = MODULE_REGISTRY[m.key] ?? { icon: MODULE_FALLBACK_ICON, color: MODULE_FALLBACK_COLOR };
            const Icon = meta.icon;
            return (
              <div
                key={m.key}
                className="panel panel-pad-lg"
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)]"
                    style={{ backgroundColor: meta.color + "22", color: meta.color }}
                  >
                    <Icon size={16} />
                  </div>
                  <div className="text-[12px] font-semibold leading-tight">{m.title}</div>
                </div>
                {m.description && (
                  <p className="mt-2 text-[11px] text-[var(--textMuted)] line-clamp-2 leading-relaxed">
                    {m.description}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-[var(--radius-lg)] border border-dashed border-[var(--border)] bg-[var(--surface)] p-6 text-center text-[12px] text-[var(--textMuted)]">
        Здесь появятся KPI и сводки отдела «{dept?.title}»
      </div>
    </div>
  );
}

// ── Main workspace ────────────────────────────────────────────────────────────

const PANEL_STORAGE_KEY = "company-portal:modules-panel-collapsed";
// activeModuleStorageKey и activeSubStorageKey переехали в lib/dept-nav.ts —
// ими пользуется и сайдбар.

export function DepartmentsWorkspace({ deptKey }: { deptKey: string }) {
  const { user } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [modules, setModules] = useState<PortalModule[]>([]);
  const [activeModuleKey, setActiveModuleKey] = useState<string | null>(null);
  const [activeSubKey, setActiveSubKey] = useState<string | undefined>(undefined);
  const [tabDir, setTabDir] = useState<TabDirection>("fwd");
  const [loading, setLoading] = useState(true);
  const [panelCollapsed, setPanelCollapsed] = useState(true);
  // Приложения (встроенные ссылки) — правая панель отдела. Три уровня видимости
  // приходят с бэка уже отфильтрованными под текущего пользователя.
  const [appEmbeds, setAppEmbeds] = useState<AppEmbed[]>([]);
  const [appEmbedsLoading, setAppEmbedsLoading] = useState(true);
  // Добавлять в отдел может любой, у кого есть доступ к отделу (решение Олега):
  // свой отдел или админ. Личное — всегда можно, общий список — только админ (бэк enforce'ит).
  const canAddAppToDept = user?.role === "platform_admin" || user?.department === deptKey;
  const canAddAppGlobal = user?.role === "platform_admin";
  const reloadAppEmbeds = useCallback(() => {
    setAppEmbedsLoading(true);
    api.listAppEmbeds(deptKey)
      .then(setAppEmbeds)
      .catch(() => setAppEmbeds([]))
      .finally(() => setAppEmbedsLoading(false));
  }, [deptKey]);
  useEffect(() => { reloadAppEmbeds(); }, [reloadAppEmbeds]);
  const [openFlowId, setOpenFlowId] = useState<number | null>(null);
  const [reviewsSummary, setReviewsSummary] = useState<WbReviewSummary | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  // Мобила: правая панель по умолчанию свёрнута в квадрат-кнопку; тап раскрывает.
  const [mobileAsideOpen, setMobileAsideOpen] = useState(false);

  // Отдел — фикс-высота (h-dvh overflow-hidden), тело страницы НЕ скроллится. Глобальный
  // html { scrollbar-gutter: stable } (анти-прыжок для скроллящихся страниц — главная, standalone
  // /modules/*) здесь резервирует мёртвую полосу справа. На время показа отдела снимаем её;
  // на unmount (уход на скроллящуюся страницу) возвращаем.
  useEffect(() => {
    const el = document.documentElement;
    const prev = el.style.scrollbarGutter;
    el.style.scrollbarGutter = "auto";
    return () => { el.style.scrollbarGutter = prev; };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Порог = Tailwind `lg` (app-shell переключает мобильный вид на <1024px):
    // иначе на 768–1023px app-shell уже мобильный (☰-квадрат), а отдел ещё десктоп
    // → табы без поля лезут под ☰, и старая правая панель вместо квадрата.
    const mq = window.matchMedia("(max-width: 1023px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const s = window.localStorage.getItem(PANEL_STORAGE_KEY);
    setPanelCollapsed(s == null ? true : s === "1");
  }, []);

  // Отделы с табовым layout фиксируют высоту страницы под viewport — отключаем
  // body+html scroll, чтобы внешняя полоса прокрутки не появлялась.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (TABBED_DEPARTMENTS.has(deptKey)) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      // На cleanup всегда сбрасываем в дефолт портала (""), а не в захваченное
      // prev-значение: из-за Strict Mode / порядка эффектов prev мог оказаться
      // "hidden", и блокировка скролла протекала на другие страницы
      // (напр. «Конструктор темы» переставал скроллиться колесом).
      return () => {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
      };
    }
  }, [deptKey]);

  function togglePanel() {
    setPanelCollapsed((v) => {
      const next = !v;
      window.localStorage.setItem(PANEL_STORAGE_KEY, next ? "1" : "0");
      return next;
    });
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([api.getDepartments(), api.getModules()])
      .then(([depts, mods]) => {
        setDepartments(depts);
        setModules(mods);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.getWbSummary().then(setReviewsSummary).catch(() => {});
  }, []);

  // Restore saved module + subtab when department changes.
  // Если переход инициирован кликом по отделу в меню — открываем дашборд
  // (для tabbed) / дефолтный модуль, игнорируя сохранённое. Пишем выбор в
  // localStorage сразу, чтобы эффект авто-выбора ниже не перетёр его.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Переезд на «подраздел помнится по разделу»: старое значение достаётся
    // последнему открытому разделу, дальше живём только по новым ключам.
    migrateLegacyActiveSub(deptKey, window.localStorage.getItem(activeModuleStorageKey(deptKey)));
    if (consumeOpenDeptDashboard(deptKey)) {
      setActiveSubKey(undefined);
      if (hasDeptDashboard(deptKey)) {
        setActiveModuleKey(DASHBOARD_KEY);
        window.localStorage.setItem(activeModuleStorageKey(deptKey), DASHBOARD_KEY);
      } else {
        setActiveModuleKey(null);
        window.localStorage.removeItem(activeModuleStorageKey(deptKey));
      }
      return;
    }
    const saved = window.localStorage.getItem(activeModuleStorageKey(deptKey));
    setActiveModuleKey(saved);
    setActiveSubKey(readActiveSub(deptKey, saved));
  }, [deptKey]);

  // ⚠️ ПОДРАЗДЕЛ СОХРАНЯЕТСЯ ТАМ, ГДЕ ЕГО ВЫБРАЛИ (chooseSub ниже), а не эффектом
  // по activeSubKey. Эффект не знает, к какому разделу относится значение: при
  // авто-переключении раздела он успевал записать чужой подраздел под ключ
  // нового и портил память раздела.

  const dept = useMemo(
    () => departments.find((d) => d.key === deptKey) ?? null,
    [departments, deptKey],
  );

  // Выбор модуля в сайдбаре, когда отдел уже открыт: роут не меняется, поэтому
  // страница узнаёт о выборе событием (см. markOpenDeptModule в lib/dept-nav).
  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<{ deptKey: string; moduleKey: string; subKey?: string }>).detail;
      if (!d || d.deptKey !== deptKey) return;
      setActiveModuleKey(d.moduleKey);
      // Без явного подраздела берём запомненный ДЛЯ ЭТОГО раздела; чего нет —
      // разрешит resolveSubKey в рендере (первый видимый подраздел).
      setActiveSubKey(d.subKey ?? readActiveSub(deptKey, d.moduleKey));
    };
    window.addEventListener(OPEN_MODULE_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_MODULE_EVENT, onOpen);
  }, [deptKey]);

  const deptModules = useMemo(
    () =>
      modules.filter(
        (m) =>
          !STANDALONE_MODULES.has(m.key) &&
          m.departmentKeys.includes(deptKey) &&
          // В tab-режиме отдела гейтинг переносится на уровень вкладок (ниже),
          // поэтому модуль здесь не отсекаем по module-view; иначе — прежнее поведение.
          (deptUsesTabPerms(user, deptKey) || hasPermission(user, m.key, "view")) &&
          m.key in MODULE_COMPONENTS,
      ),
    [modules, deptKey, user],
  );

  /** Ключи ВИДИМЫХ разделов отдела — тот же расчёт, что у сайдбара. */
  const sectionKeys = useMemo(
    () => visibleDeptTabs(deptKey, deptModules, user).map((t) => t.key),
    [deptKey, deptModules, user],
  );

  // Auto-select: восстанавливаем сохранённый раздел, иначе первый доступный.
  //
  // ⚠️ ПОЧЕМУ СВЕРЯЕМ С sectionKeys, А НЕ С МОДУЛЯМИ. Раньше сохранённый ключ
  // принимался, только если он есть среди модулей бэкенда или в PSEUDO_KEYS.
  // Но разделы HR («Найм», «Оргструктура», «Оформление», «Отпуска») —
  // синтетические: в модулях их нет, на «__» они не начинаются. Ключ не проходил
  // проверку и КАЖДАЯ перезагрузка выбрасывала на дашборд отдела
  // (Максим, 27.08.2026). Сверяем со списком реально видимых разделов —
  // единственным честным источником.
  useEffect(() => {
    // Ждём загрузки модулей — иначе при пустом списке дефолт перетирает
    // восстановленный из localStorage раздел и «залипает» на дашборде.
    if (loading) return;
    const keys = new Set(sectionKeys);
    if (activeModuleKey && keys.has(activeModuleKey)) return;
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(activeModuleStorageKey(deptKey)) : null;
    const defaultKey = hasDeptDashboard(deptKey) ? DASHBOARD_KEY : (sectionKeys[0] ?? null);
    setActiveModuleKey(saved && keys.has(saved) ? saved : defaultKey);
  }, [sectionKeys, deptKey, loading]);

  // Публикуем активную вкладку в синглтон для AI-ассистента (см. lib/active-view).
  // Значение вычисляется в теле рендера ниже (после раннего return `if (loading)`)
  // и пишется в ref; эффект без deps синхронизирует ref → синглтон после каждого
  // рендера, а очистка на размонтировании сбрасывает вкладку при уходе со страницы.
  const activeViewRef = useRef("");
  useEffect(() => { setActiveView(activeViewRef.current); });
  useEffect(() => () => setActiveView(""), []);

  // Высота РАЗВЁРНУТОЙ правой панели меряется на СКРЫТОЙ копии (см. ниже
  // measureRef) — поэтому известна всегда, даже когда панель свёрнута. Ею задаём
  // высоту рамки в ОБОИХ состояниях, и панель не прыгает при разворачивании
  // (правка Олега 22.07: свёрнутая до/после разворота была разной высоты).
  const [expandedH, setExpandedH] = useState(0);
  // Callback-ref: срабатывает КОГДА скрытая измерялка реально смонтирована. Она
  // появляется в DOM после раннего `if (loading)`, поэтому обычный useEffect[deps]
  // ловил ещё пустой ref, и высота с задержкой оставалась 0 (гонка загрузки).
  const roRef = useRef<ResizeObserver | null>(null);
  const measureRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    if (!el) return;
    const measure = () => setExpandedH(el.scrollHeight);
    measure();
    roRef.current = new ResizeObserver(measure);
    roRef.current.observe(el);
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-[var(--textMuted)]">
        <IconSpinner size={22} className="animate-spin" />
      </div>
    );
  }

  function selectModule(key: string) {
    setActiveModuleKey(key);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(activeModuleStorageKey(deptKey), key);
      setActiveSubKey(readActiveSub(deptKey, key));
    }
  }

  const ActiveComponent = activeModuleKey ? MODULE_COMPONENTS[activeModuleKey] : null;

  // ── Новый layout с горизонтальными вкладками сверху ─────────────────────────
  if (TABBED_DEPARTMENTS.has(deptKey)) {
    // Делим модули на «вкладки» (сверху) и «остальные» (правая панель).
    // Для Sales (2026-05-22): модуль Продаж не показываем как отдельную вкладку —
    // все его группы подняты на верхний уровень (РНП / Финансы / Товары / Склад).
    const isSalesHiddenWb = (m: PortalModule) =>
      deptKey === "sales" && m.key === "sales";
    // В Маркетинге вкладка-модуль «Отчёты креаторов» скрыта (04.07): её содержимое
    // разнесено по верхним вкладкам (Креаторы/Блогеры/Бартеры/Интеграции) и кнопкам тулбара.
    const isMarketingHiddenCr = (m: PortalModule) =>
      deptKey === "marketing" && m.key === "creators";
    // HR: модуль `hr` не показываем как отдельную вкладку — его 6 разделов подняты
    // в синтет-вкладки отдела (HR_DECOR / MODULE_SUBTABS.hr), рендерит их HrPage.
    const isHrHiddenModule = (m: PortalModule) => deptKey === "hr" && m.key === "hr";
    // Список разделов отдела — из ЕДИНОГО расчёта (lib/dept-sections): его же
    // показывает сайдбар. Здесь остаётся только то, что зависит от состояния
    // страницы: приложения-эмбеды и разрешение активного раздела.
    // Правка Олега 21.07: ВСЕ модули отдела живут в панели вкладок — деления на
    // «вкладочные» и «боковые» больше нет; правый бар освобождён под приложения.
    const sideModules: typeof deptModules = [];
    const visibleTabs: ModuleTabItem[] = visibleDeptTabs(deptKey, deptModules, user).map((t) => ({
      key: t.key,
      label: t.label,
      hint: t.hint,
      subItems: toSubItems(t.children),
    }));
    const visibleSide: typeof deptModules = [];
    const tabMode = deptUsesTabPerms(user, deptKey);
    const visKeys = new Set<string>([...visibleTabs.map((t) => t.key), ...appEmbeds.map((a) => appTabKey(a.id))]);
    const resolvedActive =
      activeModuleKey && visKeys.has(activeModuleKey)
        ? activeModuleKey
        : (visibleTabs[0]?.key ?? null);
    const activeTabItem = visibleTabs.find((t) => t.key === resolvedActive);
    // ⚠️ ОДИН ОТВЕТ НА ВОПРОС «КАКОЙ ПОДРАЗДЕЛ ОТКРЫТ» — resolvedSub. Ниже он идёт
    // и в полосу подразделов (подсветка), и в модуль (initialTab), и в права, и в
    // подпись для ассистента. Пока подсветка бралась из `subItems[0]`, а модулю
    // уходил сырой activeSubKey (часто undefined — его стирал клик по разделу в
    // меню), отмеченное и открытое расходились: в «Ассортименте» подсвечивался
    // «Ассортимент», а показывался «Дашборд» — экран, которого нет ни в полосе,
    // ни в матрице прав.
    const resolvedSub = resolveSubKey(activeTabItem?.subItems, activeSubKey);
    // Сайдбару нужен ТОТ ЖЕ resolvedActive, по которому рендерится содержимое,
    // иначе подсветка в меню разойдётся с тем, что открыто на экране.
    publishActiveSection(deptKey, resolvedActive, resolvedSub);
    // Выбор подраздела человеком — сюда: запоминаем его ЗА РАЗДЕЛОМ, чтобы
    // возвращение в раздел открывало то, на чём остановились.
    const chooseSub = (key: string) => {
      setActiveSubKey(key);
      if (typeof window !== "undefined" && resolvedActive) writeActiveSub(deptKey, resolvedActive, key);
    };
    const ActiveEmbeddedComp = (resolvedActive ? MODULE_COMPONENTS[resolvedActive] : null) as
      | React.ComponentType<{ embedded?: boolean; initialTab?: string; onSubtabChange?: (subKey: string) => void; canEdit?: boolean; canDelete?: boolean }>
      | null;
    // Права на действия (edit/delete) активной вкладки — Фаза 3. В tab-режиме считаем по вкладке,
    // иначе undefined → модуль сам решает по module-level (легаси/standalone не меняются).
    const tabCanEdit: boolean | undefined =
      user?.role === "platform_admin" ? true : tabMode ? canViewTab(user, deptKey, resolvedActive ?? "", resolvedSub, "edit") : undefined;
    const tabCanDelete: boolean | undefined =
      user?.role === "platform_admin" ? true : tabMode ? canViewTab(user, deptKey, resolvedActive ?? "", resolvedSub, "delete") : undefined;

    // Модуль рендерим с пропсами embedded + initialTab (через ActiveEmbeddedComp выше — по resolvedActive),
    // чтобы он скрыл свой собственный заголовок/таб-бар и слушался выбранного суб-таба.

    // Right panel — overlay поверх main column. Контент сжимается под текущую
    // ширину aside, между ними фиксированный зазор 12px (+ 8px под scrollbar).
    // Если модулей для правой панели нет — aside не рендерим, контент идёт
    // на всю ширину окна.
    const hasSideModules = visibleSide.length > 0;
    // На mobile показываем только коллапс-полоску с иконками (48px) —
    // развёрнутая 216px-панель отъела бы половину экрана. Так юзер
    // всегда может переключиться на другой модуль отдела.
    // Панель нужна ВСЕГДА: даже с пустым списком в ней живёт «Добавить приложение».
    // (hasSideModules остался от модулей — они уехали в верхний бар.)
    // 03.08: Олег попросил ВРЕМЕННО спрятать правый бар — приложениями пока не
    // пользуются, а рельс отъедал ширину у таблиц. Вернуть = снять `&& false`
    // (данные приложений в БД не трогаем, панель вернётся со своим списком).
    const showAside = false;
    // Панель — ОВЕРЛЕЙ: узкий рельс по умолчанию. НАВЕДЕНИЕ ЕЁ НЕ РАСКРЫВАЕТ
    // (правка Олега 21.07 — как и у левого рельса): разворот только кнопкой-шевроном
    // (`togglePanel`), состояние запоминается. У значков модулей свёрнутой полоски
    // подписи в `title`, так что вслепую тыкать не приходится.
    const effectiveCollapsed = isMobile ? false : panelCollapsed;
    // Ширина aside через inline style — ПИКСЕЛЬНО точно, независимо от темы.
    // Свёрнутая = 48px (= Tailwind w-16 при rem=12, как у левого sidebar).
    const asidePx = showAside ? (effectiveCollapsed ? 48 : 216) : 0;
    // Контент резервирует ФАКТИЧЕСКУЮ ширину панели (+ поля). Раньше резервировался
    // только рельс (48+отступы), потому что развёрнутая панель была ХОВЕР-оверлеем и
    // раздвигать под неё лейаут значило бы дёргать вёрстку под курсором. С 21.07
    // разворот постоянный (по шеврону) — панель больше не накрывает контент.
    // На мобиле правая панель — плавающий квадрат-оверлей (ширину не резервирует),
    // поэтому правый отступ маленький; зазор под квадрат даёт mr у пилюли табов.
    // Скролл во внутренней обёртке (тонкий auto-hide 4px), внешний контейнер НЕ скроллит
    // → широкий отступ под старый внешний скроллбар не нужен.
    const contentPadR = isMobile ? 12 : (showAside ? asidePx + 30 : 8);

    // Публикуем активную вкладку в синглтон — AI-ассистент читает её при отправке,
    // чтобы понимать не только отдел (из pathname), но и конкретную вкладку.

    // Лейбл активной вкладки — в ref (ниже раннего return `if (loading)`, поэтому
    // публикуем не хуком здесь, а эффектами, объявленными до этого return).
    activeViewRef.current = activeTabItem
      ? (() => { const sub = resolvedSub ? findSubLabel(activeTabItem.subItems, resolvedSub) : ""; return sub ? `${activeTabItem.label} → ${sub}` : activeTabItem.label; })()
      : "";

    return (
      <div className="relative -mx-4 -my-5 lg:-mx-6 lg:-my-6 h-dvh overflow-hidden"
        // Мобильный: снимаем отступ сверху, который main держит для страниц БЕЗ
        // баров отдела. Здесь бары прибиты к окну и рисуются поверх, поэтому
        // содержимое должно начинаться от края экрана — иначе над ним остаётся
        // полоса фона, а контент при прокрутке обрывается, не доходя до баров.
        // -62 — ровно padding-top у main: inline-стиль ЗАМЕНЯЕТ класс -my-5,
        // а не складывается с ним, поэтому вычитать 15 не нужно.
        style={isMobile ? { marginTop: -62 } : undefined}>
        {/* ── Main column: tab-bar + module content (full-width окна) ────── */}
        <div className="absolute inset-0">
          <div className="h-full min-w-0 overflow-hidden flex flex-col pl-3 transition-[padding] duration-200 ease-out" style={{ paddingRight: contentPadR }}>
          {/* Мобильный: ряд вкладок прибит к окну на одну линию с кнопкой ☰ (она fixed
              top-2 left-3 в app-shell), между ней и правым тулбаром. Sticky здесь не
              годится: контент получил отступ сверху (pt-[62px] в main), ряд уезжал
              вместе с ним и вставал ПОД кнопкой, а на линию возвращался только после
              прокрутки. На десктопе поведение прежнее — липкий ряд в потоке. */}
          {/* ⚠️ ВЕРХНЕЙ ПОЛОСЫ ВКЛАДОК БОЛЬШЕ НЕТ (Максим, 26.08.2026).
              Навигация целиком переехала в левый сайдбар — структура референса
              PLUX: отделы списком, разделы отдела раскрытием под ними. Список
              сайдбар считает сам — тем же lib/dept-sections, что и эта страница;
              отсюда уходит только активный раздел (publishActiveSection).
              Верх освобождён под шапку страницы. */}
          {/* Шапка экрана как в референсе: НАЗВАНИЕ РАЗДЕЛА («Кандидаты»), под
              ним строка подразделов («Все кандидаты / В работе / Архив…»).
              Подразделов в меню нет: третий уровень раздувал сайдбар, а клик по
              разделу уводил в первый подраздел вместо того, чтобы открыть сам
              раздел (Максим, 26.08.2026). */}
          {/* ⚠️ ЗАГОЛОВОК РАЗДЕЛА РИСУЕТ ВСЕГДА СТРАНИЦА ОТДЕЛА, а не модуль.
              Раньше он появлялся только у разделов с подразделами, а у остальных
              заголовок печатал сам модуль — и назывался по-своему: раздел меню
              «Боты», а надпись на экране «Управление ботами». Владелец заголовка
              менялся от раздела к разделу (Максим, 27.08.2026).
              Заголовок — общим TabHeader (Benzin Bold 20px), ряд подразделов —
              общим Segmented. */}
          {activeTabItem && (
            /* px-4 — ТОТ ЖЕ горизонтальный отступ, что у содержимого модулей
                (класс `tab-top px-4`): при px-3 заголовок начинался на 3 px левее
               таблицы под ним. */
            <div className="shrink-0 px-4 pt-3">
              <TabHeader title={activeTabItem.label} subtitle={activeTabItem.hint} />
              {(activeTabItem.subItems?.length ?? 0) > 1 && (
                <Segmented
                  items={(activeTabItem.subItems ?? []).map((x) => ({ value: x.key, label: x.label }))}
                  value={resolvedSub ?? ""}
                  onChange={chooseSub}
                />
              )}
            </div>
          )}
          {/* Контент модуля скроллится ЗДЕСЬ (не во внешнем контейнере) — бар модулей
              остаётся зафиксирован сверху, а self-contained модули (таблицы h-full с
              внутренним скроллом) влезают ровно, без «мёртвого» скролла на высоту бара.
              Длинные модули (дашборды/формы) скроллятся внутри этой обёртки. */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-autohide pb-3"
            style={isMobile ? { paddingTop: 62 } : undefined}>
            {/* Прокрутка остаётся на внешнем блоке, въезжает содержимое внутри —
                иначе при каждом переключении вкладки терялась позиция скролла. */}
            {/* Содержимое раздела — внутри провайдера: заголовок уже нарисован
                выше, и TabHeader любого модуля свой печатать не станет. */}
            <ScreenTitleProvider>
            <TabPanel activeKey={resolvedActive ?? "—"} dir={tabDir}>
            {deptKey === "hr" ? (
              // HR: app-embed из «Приложений» открываем во вьюере (иначе ветка ниже
              // недостижима — эта короткозамыкает всё для HR). Прочее — HrPage (6 вкладок).
              parseAppTabKey(resolvedActive) !== null ? (
                (() => {
                  const app = appEmbeds.find((a) => a.id === parseAppTabKey(resolvedActive));
                  return app ? <AppEmbedViewer item={app} /> : (
                    <div className="flex h-64 items-center justify-center text-[12px] text-[var(--textMuted)]">
                      Приложение не найдено — возможно, его удалили.
                    </div>
                  );
                })()
              ) : (
                <HrPage embedded initialTab={resolvedActive ?? "dashboard"} onTabChange={(k) => selectModule(k)} />
              )
            ) : resolvedActive === DASHBOARD_KEY ? (
              deptKey === "sales" ? (
                <SalesDashboard userName={user?.fullName ?? undefined} />
              ) : deptKey === "rd" ? (
                <RdDashboard
                  userName={user?.fullName ?? undefined}
                  onNavigate={(moduleKey, subKey) => {
                    selectModule(moduleKey);
                    setActiveSubKey(subKey);
                  }}
                />
              ) : deptKey === "marketing" ? (
                <MarketingDashboard userName={user?.fullName ?? undefined} />
              ) : (
                <DepartmentDashboard dept={dept} modules={deptModules} />
              )
            ) : resolvedActive === REPORT_KEY ? (
              // Синтет-вкладка «Отчёт» отдела Продажи — сводный ГПР по кабинетам.
              <SalesReportTab />
            ) : resolvedActive === MPPULSE_KEY ? (
              // Вкладка «mppulse» → навигация по РАЗДЕЛАМ (subItems из SALES_SYNTH).
              // Раздел «Оцифровка» (digitization) = дашборд mppulse (SalesWorkspace
              // в режиме initialTab="mppulse" — там уже скрыты сводные KPI / тоггл режима /
              // пресеты и включён единый период + выбор кабинета). Раздел «РНП» =
              // «Рука на пульсе» mppulse — транспонированная таблица метрики×дни
              // (initialTab="mppulse-rnp"). Раздел «ОПиУ» (opiu) = КОПИЯ ОПиУ
              // сервиса mppulse (роут /pnl) на наших данных: 5 сводных карточек
              // P&L + график по месяцам + таблица статьи×месяцы
              // (initialTab="mppulse-opiu", свой помесячный диапазон). Это ЕДИНСТВЕННЫЙ
              // ОПиУ портала: параллельный расчёт «ОПиУ 10X» снят 16.08.2026 вместе
              // с ручками /opiu и /opiu/days.
              // Новые разделы (Реклама/Тепловая карта) добавляются как ветки
              // этого тернарника по activeSubKey.
              resolvedSub === "rnp"
                ? <SalesWorkspace embedded initialTab="mppulse-rnp" />
                : resolvedSub === "opiu"
                ? <SalesWorkspace embedded initialTab="mppulse-opiu" />
                : resolvedSub === "advertising"
                ? <SalesWorkspace embedded initialTab="mppulse-adv" />
                : <SalesWorkspace embedded initialTab="mppulse" />
            ) : resolvedActive === X10_KEY ? (
              // Дефолт группы «10X» — чек-лист: «РНП (10X)» снят 13.08.2026.
              <SalesWorkspace embedded initialTab={resolvedSub || "checklist"} />
            ) : resolvedActive === FINANCE_KEY ? (
              <SalesWorkspace embedded initialTab={resolvedSub || "pl"} />
            ) : resolvedActive === PRODUCTS_KEY ? (
              // Группа «Товары» держалась одной вкладкой «Все товары» (снята
              // 13.08.2026 как дубль юнит-экономики) — открываем юнит-экономику.
              <SalesWorkspace embedded initialTab={resolvedSub || "unit"} />
            ) : resolvedActive === WAREHOUSE_KEY ? (
              // «Приход товара» и «Ассортиментная матрица» — из rd_collection, свои компоненты;
              // остальные подвкладки склада — wb_analytics. Пустой выбор и скрытые подвкладки
              // (HIDDEN_SALES_SUBTABS — могли остаться в localStorage) откатываются на «Приход
              // товара»: раньше дефолтом были «Остатки», а их больше не показываем.
              (() => {
                const sub = resolvedSub && !isHiddenSalesSub(WAREHOUSE_KEY, resolvedSub) ? resolvedSub : "incoming";
                return sub === "incoming" ? <IncomingGoodsTab />
                  : sub === "matrix" ? <SeasonAssortmentTab />
                  : sub === "wh-stock" ? <WarehouseStockTab />
                  : <SalesWorkspace embedded initialTab={sub} />;
              })()
            ) : resolvedActive === CREATORS_KEY ? (
              <CreatorReportsWorkspace embedded initialTab="reports" />
            ) : resolvedActive === COMMERCE_KEY ? (
              <MarketingTab fixedChannel="commerce" />
            ) : resolvedActive === BARTERS_KEY ? (
              <MarketingTab fixedChannel="barters" />
            ) : resolvedActive === INTEGRATIONS_KEY ? (
              <IntegrationsTab />
            ) : resolvedActive === BLOGGERS_KEY ? (
              <PartnerTab kind="blogger" />
            ) : resolvedActive === BARTERERS_KEY ? (
              <PartnerTab kind="barterer" />
            ) : parseAppTabKey(resolvedActive) !== null ? (
              (() => {
                const app = appEmbeds.find((a) => a.id === parseAppTabKey(resolvedActive));
                return app ? <AppEmbedViewer item={app} /> : (
                  <div className="flex h-64 items-center justify-center text-[12px] text-[var(--textMuted)]">
                    Приложение не найдено — возможно, его удалили.
                  </div>
                );
              })()
            ) : ActiveEmbeddedComp ? (
              <ActiveEmbeddedComp embedded initialTab={resolvedSub} onSubtabChange={chooseSub} canEdit={tabCanEdit} canDelete={tabCanDelete} />
            ) : dept ? (
              <DepartmentOverview dept={dept} moduleCount={deptModules.length} />
            ) : (
              <div className="flex h-64 items-center justify-center text-[var(--textMuted)] text-[12px]">
                Отдел не найден
              </div>
            )}
            </TabPanel>
            </ScreenTitleProvider>
          </div>
          </div>
        </div>

        {/* ── Right panel: overlay поверх main column. Сдвиг на 8px от правого
              края окна — чтобы освободить полосу под scrollbar контента.
              Рендерим только если есть модули для правой панели. */}
        {/* Mobile: квадрат-кнопка (панель свёрнута) — раскрывает правый тулбар.
            Тот же стиль, что ☰ слева и табы-пилюля. */}
        {showAside && isMobile && !mobileAsideOpen && (
          <button
            type="button"
            data-dept-aside
            onClick={() => setMobileAsideOpen(true)}
            onMouseEnter={() => setMobileAsideOpen(true)}
            aria-label="Открыть панель отдела"
            className="fixed top-2 right-3 inline-flex h-[47px] w-[47px] items-center justify-center text-[var(--text)]" style={{ zIndex: Z.NAV + 1, borderRadius: "var(--radius-md)",
              background: "var(--nav-top-bg, color-mix(in srgb, var(--bg) 68%, transparent))",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: "1px solid color-mix(in srgb, var(--text) 10%, transparent)",
              boxShadow: "0 14px 34px -14px rgba(0,0,0,0.4), inset 0 1px 0 color-mix(in srgb, #ffffff 8%, transparent)" }}
          >
            <LayoutGrid size={20} />
          </button>
        )}
        {/* Mobile: прозрачный слой — клик мимо панели закрывает её (как drawer). */}
        {showAside && isMobile && mobileAsideOpen && (
          <div className="fixed inset-0" style={{ zIndex: Z.NAV - 1 }} onClick={() => setMobileAsideOpen(false)} />
        )}
        {/* Скрытая копия РАЗВЁРНУТОЙ панели — только чтобы измерить её высоту
            (нужна и когда панель свёрнута, иначе высота неизвестна до первого
            разворота и панель прыгает). Не видна, не кликается. Ширина = развёрнутой
            внутренней (216px), чтобы переносы названий совпали. */}
        {showAside && !isMobile && (
          <div aria-hidden className="pointer-events-none absolute -z-10 opacity-0"
               style={{ right: 0, top: 0, width: 216, visibility: "hidden" }}>
            <div ref={measureRef} className="w-full">
              {/* Спейсер = высоте шапки развёрнутой панели (реальная 61px = 682−621).
                  Так измеренная высота = полной развёрнутой рамке, и minHeight
                  свёрнутой дотягивает её точно. Клонировать содержимое шапки не
                  нужно — важна только высота. */}
              <div style={{ height: 61 }} />
              <AppEmbedsPanel
                items={appEmbeds} loading={appEmbedsLoading} collapsed={false} deptKey={deptKey}
                activeKey={resolvedActive} onSelect={() => {}}
                onChanged={() => {}} canAddToDept={canAddAppToDept} canAddGlobal={canAddAppGlobal}
              />
            </div>
          </div>
        )}
        {showAside && (!isMobile || mobileAsideOpen) && (
        <aside
          data-dept-aside
          className={`absolute top-0 right-0 h-dvh flex flex-col justify-start z-nav pt-3 pb-3 px-3 transition-all duration-200 pointer-events-none`}
          style={{ width: asidePx + 24 }}
        >
          <div
            /* Прокрутка нужна и свёрнутой полоске: с 17 приложениями последние
               значки и «+» уезжали за нижний край экрана без возможности достать. */
            className="w-full pointer-events-auto flex flex-col overflow-y-auto scrollbar-autohide"
            style={{
              // Резерв под полосу прокрутки — с ОБЕИХ сторон, иначе значки свёрнутой
              // панели съезжают влево на ширину полосы (правка Олега 22.07: «справа
              // отступ больше»).
              scrollbarGutter: "stable both-edges",
              maxHeight: "calc(100dvh - 80px)",
              // Свёрнутая рамка дотягивается до высоты развёрнутой (её меряет скрытая
              // копия) — панель одной высоты в обоих состояниях, с первого рендера.
              minHeight: effectiveCollapsed && expandedH
                ? Math.min(expandedH, (typeof window !== "undefined" ? window.innerHeight : expandedH + 80) - 80)
                : undefined,
              borderRadius: "var(--nav-aside-radius, var(--radius-lg))",
              background: "var(--nav-aside-bg, color-mix(in srgb, var(--surface) 68%, transparent))",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: "1px solid color-mix(in srgb, var(--text) 10%, transparent)",
              boxShadow: "0 18px 44px -12px rgba(0,0,0,0.45), inset 0 1px 0 color-mix(in srgb, #ffffff 10%, transparent)",
            }}
          >
          {/* key по состоянию: при разворачивании/сворачивании контент плавно
              проявляется (yuc-fade), ширина рамки анимируется рядом (transition-all
              на aside). Высоты одинаковы, поэтому только fade — без прыжка. */}
          <div key={effectiveCollapsed ? "collapsed" : "expanded"} className="yuc-fade flex min-h-0 flex-1 flex-col">
          {effectiveCollapsed ? (
            <div className="flex flex-1 flex-col items-center gap-1 py-3">
              {!isMobile && (
              <button
                type="button"
                onClick={togglePanel}
                data-tooltip="Развернуть панель"
                aria-label="Развернуть панель"
                className="glass-flat mx-auto flex h-9 w-9 items-center justify-center rounded-full text-[var(--textMuted)] transition-colors hover:text-[var(--text)]"
              >
                <ChevronsLeft size={20} className="shrink-0" />
              </button>
              )}
              <div className={`w-full flex-1 px-1.5 ${isMobile ? "mt-0" : "mt-1"}`}>
                <AppEmbedsPanel
                  items={appEmbeds} loading={appEmbedsLoading} collapsed deptKey={deptKey}
                  activeKey={resolvedActive} onSelect={selectModule}
                  onChanged={reloadAppEmbeds} canAddToDept={canAddAppToDept} canAddGlobal={canAddAppGlobal}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="sticky top-0 border-b border-[var(--border)] px-3 py-2.5 z-10 flex items-center justify-between gap-1" style={{ backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", background: "var(--nav-aside-bg, color-mix(in srgb, var(--surface) 68%, transparent))" }}>
                {/* Панель узкая (240px): название отдела здесь лишнее — оно и так в
                    шапке страницы. Одна строка «ПРИЛОЖЕНИЯ · N», иначе счётчик
                    переносился на вторую строку (правка Олега 21.07). */}
                <div className="flex min-w-0 items-baseline gap-1.5">
                  <span className="truncate text-[11px] text-[var(--textMuted)]">
                    Приложения
                  </span>
                  {appEmbeds.length > 0 && (
                    <span className="shrink-0 text-[11px] font-semibold leading-none">{appEmbeds.length}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={isMobile ? () => setMobileAsideOpen(false) : togglePanel}
                  data-tooltip={isMobile ? "Закрыть панель" : "Свернуть панель"}
                  /* Только шеврон: слово «Свернуть» съедало половину шапки узкой
                     панели (103px из 210). Смысл несёт title, а рядом с ним —
                     тот же шеврон, что у левого рельса. */
                  className="glass-flat inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[var(--textMuted)] transition-colors hover:text-[var(--text)]"
                >
                  <ChevronsRight size={15} className="shrink-0" />
                </button>
              </div>

              <AppEmbedsPanel
                items={appEmbeds} loading={appEmbedsLoading} collapsed={false} deptKey={deptKey}
                activeKey={resolvedActive} onSelect={selectModule}
                onChanged={reloadAppEmbeds} canAddToDept={canAddAppToDept} canAddGlobal={canAddAppGlobal}
              />
            </>
          )}
          </div>
          </div>
        </aside>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100dvh-48px)] gap-0 -mx-4 -my-5 lg:-mx-6 lg:-my-6">
      {/* ── Center: module content ─────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 px-6 py-6">
        {activeModuleKey === "bots" && openFlowId !== null ? (
          <FlowBuilderPage flowId={openFlowId} onClose={() => setOpenFlowId(null)} />
        ) : activeModuleKey === "bots" ? (
          <TelegramWorkspace onOpenFlow={setOpenFlowId} />
        ) : ActiveComponent ? (
          <ActiveComponent />
        ) : dept ? (
          <DepartmentOverview dept={dept} moduleCount={deptModules.length} />
        ) : (
          <div className="flex h-64 items-center justify-center text-[var(--textMuted)] text-[12px]">
            Отдел не найден
          </div>
        )}
      </div>

      {/* ── Right panel: module cards.
            На mobile показываем только коллапс-полоску (48px) — модули
            доступны кликом по иконкам без развёртки. ────────────────────── */}
      {(() => {
        // Локальное определение, дублирующее логику внутри tabbed-блока,
        // чтобы non-tabbed return тоже корректно работал на мобиле.
        const fxColl = panelCollapsed || isMobile;
        return (
      <aside
        className={`flex-shrink-0 border-l border-[var(--border)] bg-[var(--surface)] flex flex-col sticky top-0 h-dvh transition-all duration-200 ${fxColl ? "" : "overflow-y-auto"}`}
        style={{ width: fxColl ? 48 : 216 }}
      >

        {fxColl ? (
          /* ── Collapsed: icon strip ── */
          <div className="flex flex-col items-center gap-1 py-3">
            {!isMobile && (
            <button
              type="button"
              onClick={togglePanel}
              data-tooltip="Развернуть панель"
              aria-label="Развернуть панель"
              className="glass-flat mx-auto flex h-9 w-9 items-center justify-center rounded-full text-[var(--textMuted)] transition-colors hover:text-[var(--text)]"
            >
              <ChevronsLeft size={20} className="shrink-0" />
            </button>
            )}
            <div className={`w-full px-1.5 space-y-1 ${isMobile ? "mt-0" : "mt-1"}`}>
              {deptModules.map((mod) => {
                const meta = MODULE_REGISTRY[mod.key] ?? { icon: MODULE_FALLBACK_ICON, color: MODULE_FALLBACK_COLOR };
                const Icon = meta.icon;
                const active = activeModuleKey === mod.key;
                return (
                  <button
                    key={mod.key}
                    type="button"
                    data-tooltip={mod.title}
                    onClick={() => { selectModule(mod.key); }}
                    className={`flex w-full items-center justify-center rounded-[var(--radius-md)] p-2 transition glass-hover ${active ? "bg-[color-mix(in_srgb,var(--primary)_12%,transparent)]" : ""}`}
                    style={{ color: active ? meta.color : undefined }}
                  >
                    <Icon size={17} style={{ color: meta.color }} />
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* ── Expanded: full panel ── */
          <>
            <div className="sticky top-0 bg-[var(--surface)] border-b border-[var(--border)] px-4 py-3.5 z-10 flex items-start justify-between gap-2">
              <div>
                <div className="field-label">
                  {dept?.title ?? "Отдел"}
                </div>
                <div className="text-[12px] font-semibold mt-0.5">
                  {deptModules.length > 0
                    ? `${deptModules.length} ${deptModules.length === 1 ? "модуль" : deptModules.length < 5 ? "модуля" : "модулей"}`
                    : "Нет модулей"}
                </div>
              </div>
              <button
                type="button"
                onClick={togglePanel}
                data-tooltip="Свернуть панель"
                className="glass-flat mt-0.5 inline-flex flex-shrink-0 items-center gap-2.5 rounded-[var(--radius-md)] px-3 py-2 text-[12px] text-[var(--textMuted)] transition-colors hover:text-[var(--text)]"
              >
                <ChevronsRight size={17} className="shrink-0" />
                <span className="truncate">Свернуть</span>
              </button>
            </div>

            <div className="p-3 space-y-2">
              {deptModules.length === 0 ? (
                <div className="py-8 text-center text-[11px] text-[var(--textMuted)]">
                  Нет доступных модулей
                </div>
              ) : (
                deptModules.map((mod) => (
                  <ModuleCard
                    key={mod.key}
                    module={mod}
                    active={activeModuleKey === mod.key}
                    onClick={() => selectModule(mod.key)}
                    reviewsSummary={reviewsSummary}
                  />
                ))
              )}
            </div>
          </>
        )}
      </aside>
        );
      })()}
    </div>
  );
}
